import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { Mediator } from '@spotahome/soyuz-mediator';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import {
  BILLS_INCLUDED_VALUES,
  ValuePropositionFeatures as ValuePropositionFeaturesList,
  isPlusPlan
} from '@spotahome/ui-library';

import { SHOW_EXTENDED_LANDLORD_INFORMATION } from '@spotahome/marketplace-common/src/channels';

import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import { getBillsIncluded } from '@spotahome/ui-library/src/utils/listing';

import { useLandlordInformation } from '../context/LandlordInformationContext';

const ValuePropositionFeatures = ({
  listing,
  dataTest = 'book-now-features'
}) => {
  const {
    instantBooking,
    checked,
    bills,
    allowsPermanentContract,
    isBestChoice,
    plan,
    runner,
    verificationDate,
    bookingConditions
  } = listing;
  const hasDepositProtection = bookingConditions?.hasDepositProtection;
  const { isEnabled: instantBookingEnabled, rules } = instantBooking;
  const { landlordInformation } = useLandlordInformation();
  const isBadgesResponseRateVariant =
    useSoyuzExperiment('BadgesResponseRateSearchAndListing').get('behaviour') ===
    'variant';
  const [valuePropositionRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.25
  });

  const { hasNoDeposit } = usePricingContext();

  const billsIncluded = getBillsIncluded(bills);

  useEffect(() => {
    if (inView) {
      SoyuzAnalytics.sendGA4Event('value-proposition-visible', {});
    }
  }, [inView]);

  const handleLandlordTypeClick = () => {
    Mediator.publish(SHOW_EXTENDED_LANDLORD_INFORMATION);
  };

  return (
    <ValuePropositionFeaturesList
      ref={valuePropositionRef}
      isChecked={checked}
      landlordInformation={landlordInformation}
      hasNoDeposit={hasNoDeposit}
      hasDepositProtection={hasDepositProtection}
      isInstantBookingEnabled={instantBookingEnabled}
      isBillsIncluded={billsIncluded === BILLS_INCLUDED_VALUES.all}
      isSomeBillsIncluded={billsIncluded === BILLS_INCLUDED_VALUES.some}
      instantBookingRules={rules}
      onLandlordTypeClick={handleLandlordTypeClick}
      isPlus={isPlusPlan(plan)}
      isLongTerm={allowsPermanentContract}
      isBestChoice={isBestChoice}
      homechecker={runner}
      verificationDate={verificationDate}
      backgroundColor="white"
      title={trans('value.proposition.features.title')}
      dataTest={dataTest}
      responseRateState={
        isBadgesResponseRateVariant
          ? landlordInformation?.responseRateState ?? null
          : null
      }
      responseRatePercentage={
        isBadgesResponseRateVariant
          ? landlordInformation?.responseRatePercentage ?? null
          : null
      }
    />
  );
};

ValuePropositionFeatures.propTypes = {
  listing: PropTypes.shape(ListingShape).isRequired
};

export default ValuePropositionFeatures;
